/**
 * StudioPillars — bulk import content ideas via CSV/Excel.
 * Downloads template, uploads file, inserts as Idea-stage content.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Upload, Download, Loader2, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioPillars() {
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/studio/channels`, { withCredentials: true })
      .then((r) => {
        const all = [...(r.data?.owned || []), ...(r.data?.shared || [])];
        setChannels(all);
        if (all[0]) setActiveId(all[0].channel_id);
      })
      .catch(() => {});
  }, []);

  const downloadTemplate = () => {
    window.open(`${API}/api/studio/command/pillars/template.csv`, "_blank");
  };

  const submit = async () => {
    if (!file || !activeId) { toast.error("Select channel and file"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/api/studio/command/pillars/import?channel_id=${activeId}`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(r.data);
      toast.success(`Imported ${r.data.inserted} ideas`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <span className="studio-eyebrow"><FileText size={12}/> Content Pillars</span>
          <h1 className="studio-h1">
            Bulk import content ideas <span className="studio-h1-accent">via CSV.</span>
          </h1>
          <p className="studio-sub">
            Download the template, fill in titles + pillar + priority, upload — every row becomes an Idea-stage content card.
          </p>
        </div>

        <div className="studio-card" style={{ padding: 18 }}>
          <h3>1. Download template</h3>
          <p className="studio-card-sub">CSV with columns: title, format, pillar, priority, primary_keyword, notes.</p>
          <button className="studio-btn-ghost" onClick={downloadTemplate} data-testid="pillars-download">
            <Download size={14}/> Download template.csv
          </button>
        </div>

        <div className="studio-card" style={{ padding: 18, marginTop: 14 }}>
          <h3>2. Pick channel + upload</h3>
          <div className="studio-form-row" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
            <select className="studio-input" value={activeId} onChange={(e) => setActiveId(e.target.value)} data-testid="pillars-channel" style={{ minWidth: 220 }}>
              <option value="">Pick a channel…</option>
              {channels.map((c) => <option key={c.channel_id} value={c.channel_id}>{c.title}</option>)}
            </select>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="studio-input"
              data-testid="pillars-file"
              style={{ flex: 1, minWidth: 220 }}
            />
            <button className="studio-btn-primary" onClick={submit} disabled={busy || !file || !activeId} data-testid="pillars-import">
              {busy ? <Loader2 size={14} className="ac-spin"/> : <Upload size={14}/>} Import
            </button>
          </div>
        </div>

        {result && (
          <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="pillars-result">
            <h3><CheckCircle2 size={16} style={{ color: "#16a34a", verticalAlign: "-3px", marginRight: 6 }}/> Import complete</h3>
            <p><strong>{result.inserted}</strong> ideas added to pipeline.</p>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
